import React, { useState } from 'react';
import './DevReport.css';

// ── Data ──────────────────────────────────────────────────────────────────────

const SNET_PACKAGES = [
  { pkg: 'hyperon', version: '0.2.10', role: 'MeTTa interpreter — executes all inference rules against the personal knowledge graph' },
  { pkg: 'hyperon-das', version: '0.9.2', role: 'Distributed Atom Space — the graph database that stores every fact the user shares' },
  { pkg: 'hyperon-das-atomdb', version: '0.9.2', role: 'Storage adapters — InMemoryDB (Phase 1), RedisMongoDB (Phase 2)' },
  { pkg: 'snet-sdk', version: '0.4.0', role: 'Backend Python SDK for SingularityNET marketplace service calls (Phase 3)' },
  { pkg: 'snet-sdk-web', version: '5.0.5', role: 'Frontend JavaScript SDK — wired for Phase 3 marketplace integration' },
];

const DIFFERENTIATORS = [
  {
    icon: '🧠',
    title: 'Reasoning Over Memory, Not Retrieval',
    body: 'The dominant paradigm (ChatGPT with memory, Notion AI, Mem.ai) stores documents and retrieves via embedding similarity. ASI:Mind parses every fact into a typed, probabilistic atom and answers queries by running MeTTa inference rules over the graph — not asking an LLM to recall text.',
    points: [
      'Cross-domain inference: facts the system has never seen together',
      'Confidence is explicit and traceable, not hallucinated',
      'Every inference writes a ReasoningEvent atom to DAS (F-11)',
    ],
  },
  {
    icon: '💡',
    title: 'Proactive Insight Engine',
    body: 'Most personal AI tools are reactive. The InsightEngine scans the DAS every 6 hours for patterns the user hasn\'t asked about, with confidence filtering ≥ 0.65 and 7-day deduplication.',
    insights: [
      { type: 'habit_detected', example: 'Poor sleep quality in 60% of recorded nights' },
      { type: 'goal_at_risk', example: 'Goal deadline in 8 days with no recent progress' },
      { type: 'correlation_found', example: 'Sleep quality drops after heavy meeting days' },
      { type: 'anomaly_detected', example: 'No new memories in 7 days' },
      { type: 'recommendation', example: 'Proactive action from accumulated patterns' },
    ],
  },
  {
    icon: '🔐',
    title: 'Sovereign, Local-First Data Model',
    body: 'No user data is ever sent to a cloud service without explicit consent.',
    points: [
      'LLM (Mistral 7B / GPT-OSS) acts only as NL↔MeTTa translator — not the reasoning engine',
      'DAS runs entirely locally',
      'In Phase 3, only a cryptographic hash is anchored to ASI:Chain — atom contents never leave the device (F-10-R03)',
      'Dashboard protected by local PIN — no cloud login (F06-R09)',
    ],
  },
  {
    icon: '🕸️',
    title: 'Multi-Domain Cross-Domain Inference',
    body: 'The knowledge graph spans nine semantic domains. MeTTa rulesets can reference atoms across domains — the career ruleset can query SleepDuration atoms; relationships.metta can check Goal atoms.',
    code: `(= (network-opportunity $person $goal)
   (collapse
     (match &self
       (And (Goal $person $goal active)
            (GoalDomain $person $goal $domain)
            (Knows $person $contact $any)
            (WorksIn $contact $domain))
       (OpportunityContact $contact $domain))))`,
  },
  {
    icon: '🔍',
    title: 'Full Transparency on Every Inference',
    body: 'Every response shows a confidence score, an expandable reasoning chain, and a specific data sufficiency warning when the graph lacks enough context.',
    points: [
      'Confidence score on every response (e.g. "87% confidence")',
      'Expandable "Why?" reasoning chain — step-by-step MeTTa inference',
      'Sufficiency warning: "health — need 7 sleep records, have 2"',
    ],
  },
];

const GAPS = [
  {
    title: 'No end-to-end personal knowledge pipeline',
    built: 'language/nl_to_metta.py, language/metta_to_nl.py, ingestion/conversational.py (with F01-R05 contradiction detection), full 12-endpoint FastAPI gateway.',
  },
  {
    title: 'No personal domain MeTTa rulesets',
    built: '8 domain rulesets written from scratch: core, goals, health, career, finance, habits, relationships, family. The relationships.metta ruleset had no open-source equivalent and was built from the COMPONENT-DESIGN spec.',
  },
  {
    title: 'No web-based UI for Hyperon',
    built: 'React dashboard with 5 panels: Chat (reasoning transparency, WebSocket), Knowledge Graph (D3.js force-directed, click-to-inspect), Goals, Insights feed (dismiss/snooze/act lifecycle), local PIN auth.',
  },
  {
    title: 'No persistent personal memory schema',
    built: '9 Domain enums, full Pydantic hierarchy (Atom, LinkAtom, TruthValue, InsightEvent, ReasoningEvent, IngestionEvent), AtomAPI singleton with full COMPONENT-DESIGN contract, contradiction detection.',
  },
  {
    title: 'No proactive agent layer',
    built: 'InsightEngine: async 6-hour background loop, 5 detection algorithms, confidence filter ≥ 0.65, 7-day rolling deduplication, full insight lifecycle, last_run surfaced in /status (NFR-12).',
  },
];

const RULESETS = [
  { file: 'core.metta', predicates: 'Base predicates, PLN-style TruthValue combinators, implication chains' },
  { file: 'goals.metta', predicates: 'goal-progress, deadline-proximity, subgoal-completion' },
  { file: 'health.metta', predicates: 'sleep-pattern, energy-state, chronic-poor-sleep?' },
  { file: 'career.metta', predicates: 'evaluate-opportunity, career-momentum?, seniority-match?' },
  { file: 'finance.metta', predicates: 'net-worth, spending-anomaly?, savings-rate' },
  { file: 'habits.metta', predicates: 'habit-regularity, streak-length, habit-broken?' },
  { file: 'relationships.metta', predicates: 'trusted-for?, network-opportunity, relationship-stale?' },
  { file: 'family.metta', predicates: 'family-health-trend, family-engagement-score' },
];

const PHASES = [
  { phase: 'Phase 1', status: 'complete', desc: 'Fully local — Ollama / GPT-OSS LLM, InMemoryDB DAS, REST + WebSocket API, React dashboard' },
  { phase: 'Phase 2', status: 'stubbed', desc: 'Optional Redis/MongoDB persistence for larger graphs (DAS_MODE=redis_mongo)' },
  { phase: 'Phase 3', status: 'architected', desc: 'ASI:Chain on-chain ownership anchoring; SingularityNET marketplace service routing via snet-sdk' },
];

const BENEFITS = [
  { icon: '🗂️', title: 'Complete Data Sovereignty', desc: 'No user account, no cloud sync. Export your full graph as JSON any time. In Phase 3, only a cryptographic hash is anchored to ASI:Chain — never the content.' },
  { icon: '🎯', title: 'Answers Based on Your Life', desc: 'When you ask "should I take this job?", ASI:Mind answers from your career history, goals, sleep data, finances, and relevant relationships — not generic advice.' },
  { icon: '📈', title: 'Gets Smarter Over Time', desc: 'Every fact you share accumulates permanently. A question asked six months from now is answered with six months of context about your specific life.' },
  { icon: '🔔', title: 'Proactive, Not Reactive', desc: 'The insight engine watches for patterns you didn\'t think to ask about: deadlines approaching, habit drift, sleep/schedule correlations — surfaced automatically.' },
  { icon: '📊', title: 'No Hallucination', desc: 'Every claim is grounded in atoms you put there. If data is missing, the system tells you precisely what\'s needed rather than inventing a confident-sounding answer.' },
  { icon: '🌐', title: 'Bilingual', desc: 'English and Korean detected automatically by Unicode analysis. Responses are returned in the same language the question was asked in — no configuration required.' },
];

const PANELS = [
  { panel: 'Chat', purpose: 'Primary conversational interface with reasoning transparency: confidence, "Why?" chain, sufficiency warnings, WebSocket streaming' },
  { panel: 'Knowledge Graph', purpose: 'D3.js force-directed visualization of all atoms, colour-coded by domain, click-to-inspect' },
  { panel: 'Goals', purpose: 'Goal atoms with progress bars, deadline proximity, and domain distribution' },
  { panel: 'Insights', purpose: 'Proactive engine output — dismiss / snooze / act lifecycle' },
  { panel: 'Settings', purpose: 'Runtime LLM provider switch (GPT-OSS 120B ↔ Mistral 7B local) with live online/offline status' },
];

const COMMANDS = [
  { cmd: '/status', effect: 'Shows atom count, domain breakdown, LLM status, insight queue depth' },
  { cmd: '/forget <topic>', effect: 'Removes all atoms related to a topic — e.g. /forget Samsung' },
];

// ── Sections ──────────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'snet', label: '1. SingularityNET Components' },
  { id: 'diff', label: '2. Key Differentiators' },
  { id: 'gaps', label: '3. Gaps We Filled' },
  { id: 'manual', label: '4. User Manual' },
  { id: 'benefits', label: '5. User Benefits' },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function DevReport({ onClose, onBack }) {
  const [active, setActive] = useState('snet');

  const scrollTo = (id) => {
    setActive(id);
    document.getElementById(`dr-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="dr-page">

      <div className="dr-body">
        {/* ── Sidebar TOC ── */}
        <aside className="dr-toc">
          <div className="dr-toc-header">
            <div className="dr-toc-title">
              <span className="dr-logo-asi">ASI:</span><span className="dr-logo-mind">Mind</span>
              <span className="dr-toc-report"> Dev Report</span>
            </div>
            <button className="dr-close" onClick={onBack ?? onClose} title="Close">✕</button>
          </div>
          <div className="dr-toc-label">Contents</div>
          {SECTIONS.map(s => (
            <button
              key={s.id}
              className={`dr-toc-item ${active === s.id ? 'active' : ''}`}
              onClick={() => scrollTo(s.id)}
            >
              {s.label}
            </button>
          ))}
          <div className="dr-toc-meta">2 May 2026 · Yoonil Choi</div>
        </aside>

        {/* ── Content ── */}
        <div className="dr-content">

            {/* 1 — SNET Components */}
            <section id="dr-snet" className="dr-section">
              <div className="dr-section-label">SingularityNET Open Source</div>
              <h2 className="dr-section-title">Components Used</h2>

              <table className="dr-table">
                <thead>
                  <tr><th>Package</th><th>Version</th><th>Role</th></tr>
                </thead>
                <tbody>
                  {SNET_PACKAGES.map(r => (
                    <tr key={r.pkg}>
                      <td><code className="dr-code">{r.pkg}</code></td>
                      <td><span className="dr-version">{r.version}</span></td>
                      <td>{r.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h3 className="dr-sub-title">MeTTa Reasoning Engine</h3>
              <p className="dr-p">
                Every user query requiring reasoning — career decisions, goal tracking, habit patterns —
                is translated into a MeTTa expression and executed by <code className="dr-code">MeTTaEngine</code>.
                The engine loads domain-specific rulesets that encode PLN-style inference rules:
              </p>
              <pre className="dr-pre">{`; career.metta — job opportunity evaluation
(= (evaluate-opportunity $person $org)
   (collapse
     (match &self
       (And (Goal $person $goal active)
            (WorkedAt $person $prev-org $duration)
            (HasSkill $person $skill $proficiency))
       (OpportunityScore $org $goal $skill))))`}</pre>

              <h3 className="dr-sub-title">MeTTa Ruleset Files</h3>
              <table className="dr-table">
                <thead><tr><th>File</th><th>Key Predicates</th></tr></thead>
                <tbody>
                  {RULESETS.map(r => (
                    <tr key={r.file}>
                      <td><code className="dr-code">{r.file}</code></td>
                      <td>{r.predicates}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h3 className="dr-sub-title">MeTTaClaw Architecture Pattern</h3>
              <p className="dr-p">
                The <code className="dr-code">ConversationManager</code> mirrors the MeTTaClaw agent pattern
                from the Hyperon examples repository. <code className="dr-code">AtomAPI.remember()</code> mirrors
                the <code className="dr-code">(remember)</code> primitive. The full pipeline:
              </p>
              <div className="dr-pipeline">
                {['NL input', 'nl_parser.parse()', 'MeTTa expression', 'metta_engine.run_query()', 'nl_formatter.format()', 'NL response'].map((step, i, arr) => (
                  <React.Fragment key={step}>
                    <span className="dr-pipe-step">{step}</span>
                    {i < arr.length - 1 && <span className="dr-pipe-arrow">→</span>}
                  </React.Fragment>
                ))}
              </div>
            </section>

            {/* 2 — Differentiators */}
            <section id="dr-diff" className="dr-section">
              <div className="dr-section-label">What Makes It Different</div>
              <h2 className="dr-section-title">Key Differentiators</h2>

              {DIFFERENTIATORS.map(d => (
                <div key={d.title} className="dr-diff-card">
                  <div className="dr-diff-icon">{d.icon}</div>
                  <div className="dr-diff-body">
                    <h3 className="dr-diff-title">{d.title}</h3>
                    <p className="dr-p">{d.body}</p>
                    {d.points && (
                      <ul className="dr-ul">
                        {d.points.map(p => <li key={p}>{p}</li>)}
                      </ul>
                    )}
                    {d.insights && (
                      <div className="dr-insight-rows">
                        {d.insights.map(ins => (
                          <div key={ins.type} className="dr-insight-row">
                            <code className="dr-code dr-code--insight">{ins.type}</code>
                            <span>{ins.example}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {d.code && <pre className="dr-pre">{d.code}</pre>}
                  </div>
                </div>
              ))}
            </section>

            {/* 3 — Gaps */}
            <section id="dr-gaps" className="dr-section">
              <div className="dr-section-label">What We Built That Didn't Exist</div>
              <h2 className="dr-section-title">Gaps in Existing SingularityNET Projects</h2>
              <p className="dr-p dr-p--intro">
                The SingularityNET ecosystem provides powerful primitives (Hyperon, DAS, MeTTa)
                but no application layer connecting them to an end-user product.
              </p>
              <div className="dr-gaps">
                {GAPS.map((g, i) => (
                  <div key={g.title} className="dr-gap-card">
                    <div className="dr-gap-num">{String(i + 1).padStart(2, '0')}</div>
                    <div className="dr-gap-body">
                      <div className="dr-gap-problem">{g.title}</div>
                      <div className="dr-gap-solution">
                        <span className="dr-gap-built">Built: </span>{g.built}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* 4 — Manual */}
            <section id="dr-manual" className="dr-section">
              <div className="dr-section-label">Getting Started</div>
              <h2 className="dr-section-title">User Manual</h2>

              <h3 className="dr-sub-title">Starting the Application</h3>
              <pre className="dr-pre">{`# Terminal 1 — backend
cd development/codebase/backend
pip install -r requirements.txt
uvicorn main:app --reload --port 8000

# Terminal 2 — frontend
cd development/codebase/frontend
npm install
npm start   # opens http://localhost:3001`}</pre>

              <h3 className="dr-sub-title">The Five Panels</h3>
              <table className="dr-table">
                <thead><tr><th>Panel</th><th>Purpose</th></tr></thead>
                <tbody>
                  {PANELS.map(r => (
                    <tr key={r.panel}>
                      <td><strong>{r.panel}</strong></td>
                      <td>{r.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h3 className="dr-sub-title">Talking to ASI:Mind</h3>
              <p className="dr-p">No special syntax required. Examples of what you can say:</p>

              <div className="dr-examples">
                <div className="dr-example-group">
                  <div className="dr-example-label">Sharing a memory (stored as atoms)</div>
                  {[
                    '"I had a great meeting with Ibby about ASI:Mind today"',
                    '"My sleep was only 5 hours last night"',
                    '"I left Samsung in May 2022 after four years as a product manager"',
                    '"I want to join SingularityNET by the end of this year"',
                    '"I have 2 ETH and a savings account with £15,000"',
                  ].map(ex => (
                    <div key={ex} className="dr-example-chip">{ex}</div>
                  ))}
                </div>
                <div className="dr-example-group">
                  <div className="dr-example-label">Asking a question (runs MeTTa inference)</div>
                  {[
                    '"Should I join SingularityNET?"',
                    '"How are my goals progressing?"',
                    '"What patterns do you see in my sleep?"',
                    '"Am I on track financially this month?"',
                    '"Who in my network could help with my career goals?"',
                  ].map(ex => (
                    <div key={ex} className="dr-example-chip dr-example-chip--query">{ex}</div>
                  ))}
                </div>
              </div>

              <h3 className="dr-sub-title">Commands</h3>
              <table className="dr-table">
                <thead><tr><th>Command</th><th>Effect</th></tr></thead>
                <tbody>
                  {COMMANDS.map(r => (
                    <tr key={r.cmd}>
                      <td><code className="dr-code">{r.cmd}</code></td>
                      <td>{r.effect}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h3 className="dr-sub-title">Insight Actions</h3>
              <p className="dr-p">Each auto-generated insight supports four actions:</p>
              <div className="dr-action-chips">
                {[
                  { action: 'Acknowledge', desc: 'Mark as seen' },
                  { action: 'Snooze', desc: 'Hide for 24 hours' },
                  { action: 'Dismiss', desc: 'Remove permanently' },
                  { action: 'Act', desc: 'Mark as acted on' },
                ].map(a => (
                  <div key={a.action} className="dr-action-chip">
                    <span className="dr-action-name">{a.action}</span>
                    <span className="dr-action-desc">{a.desc}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* 5 — Benefits */}
            <section id="dr-benefits" className="dr-section">
              <div className="dr-section-label">Why It Matters</div>
              <h2 className="dr-section-title">User Benefits</h2>

              <div className="dr-benefits-grid">
                {BENEFITS.map(b => (
                  <div key={b.title} className="dr-benefit-card">
                    <div className="dr-benefit-icon">{b.icon}</div>
                    <h3 className="dr-benefit-title">{b.title}</h3>
                    <p className="dr-benefit-desc">{b.desc}</p>
                  </div>
                ))}
              </div>

              <h3 className="dr-sub-title">Roadmap</h3>
              <div className="dr-phases">
                {PHASES.map(p => (
                  <div key={p.phase} className={`dr-phase dr-phase--${p.status}`}>
                    <div className="dr-phase-header">
                      <span className="dr-phase-label">{p.phase}</span>
                      <span className={`dr-phase-badge dr-phase-badge--${p.status}`}>{p.status}</span>
                    </div>
                    <p className="dr-phase-desc">{p.desc}</p>
                  </div>
                ))}
              </div>
            </section>

        </div>
      </div>
    </div>
  );
}
